import { apiGet, apiPost, apiPatch } from './apiClient.js';

/**
 * Admin Notifications Service
 * For managing in-app notifications
 */

/**
 * List notifications with filters and pagination
 * @param {object} params
 * @param {boolean} [params.unreadOnly] - Filter to unread only
 * @param {string} [params.severity] - Filter by severity
 * @param {string} [params.type] - Filter by exact type
 * @param {string} [params.typePrefix] - Filter by type prefix
 * @param {string} [params.cursor] - Cursor for pagination
 * @param {number} [params.limit] - Max results
 * @param {boolean} [params.includeSnoozed] - Include snoozed notifications
 * @returns {Promise<object>} { items, nextCursor }
 */
export async function getNotifications(params = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.unreadOnly) queryParams.append('unreadOnly', 'true');
  if (params.severity) queryParams.append('severity', params.severity);
  if (params.type) queryParams.append('type', params.type);
  if (params.typePrefix) queryParams.append('typePrefix', params.typePrefix);
  if (params.cursor) queryParams.append('cursor', params.cursor);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.includeSnoozed) queryParams.append('includeSnoozed', 'true');

  const queryString = queryParams.toString();
  const response = await apiGet(`/admin/notifications${queryString ? `?${queryString}` : ''}`);
  // `apiClient` returns the unwrapped payload: { items, nextCursor, ... }
  return response;
}

/**
 * Get unread notification count
 * @returns {Promise<number>} Unread count
 */
export async function getUnreadCount() {
  const response = await apiGet('/admin/notifications/unread-count');
  return response.count || 0;
}

/**
 * Mark notification as read
 * @param {string} id - Notification ID
 * @param {string} reason - Reason for marking as read
 * @param {number} intentAuditEventId - Intent audit event ID
 * @returns {Promise<object>} Updated notification
 */
export async function markNotificationRead(id, reason, intentAuditEventId) {
  const response = await apiPost(`/admin/notifications/${id}/read`, {
    reason,
    intentAuditEventId,
  });
  return response.notification || response;
}

/**
 * Mark all notifications as read
 * @param {string} reason - Reason for marking all as read
 * @param {number} intentAuditEventId - Intent audit event ID
 * @returns {Promise<object>} { count: number }
 */
export async function markAllNotificationsRead(reason, intentAuditEventId) {
  const response = await apiPost('/admin/notifications/read-all', {
    reason,
    intentAuditEventId,
  });
  return response;
}

/**
 * List notification subscriptions
 * @returns {Promise<Array>} Subscriptions
 */
export async function getSubscriptions() {
  const response = await apiGet('/admin/notification-subscriptions');
  return response.subscriptions || [];
}

/**
 * Create or update a subscription
 * @param {object} payload
 * @param {string} payload.event_type - Event type
 * @param {object} [payload.filter_json] - Filter criteria
 * @param {boolean} [payload.is_enabled] - Enabled flag
 * @param {string} payload.reason - Reason for creating/updating
 * @param {number} payload.intentAuditEventId - Intent audit event ID
 * @returns {Promise<object>} Created or updated subscription
 */
export async function upsertSubscription(payload) {
  const response = await apiPost('/admin/notification-subscriptions', payload);
  return response.subscription || response;
}

/**
 * Update a subscription
 * @param {string} id - Subscription ID
 * @param {object} patch - Fields to update
 * @param {string} patch.reason - Reason for update
 * @param {number} patch.intentAuditEventId - Intent audit event ID
 * @returns {Promise<object>} Updated subscription
 */
export async function updateSubscription(id, patch) {
  const response = await apiPatch(`/admin/notification-subscriptions/${id}`, patch);
  return response.subscription || response;
}

/**
 * Mark notification as unread
 * @param {string} id - Notification ID
 * @param {string} reason - Reason for marking as unread
 * @param {number} intentAuditEventId - Intent audit event ID
 * @returns {Promise<object>} Updated notification
 */
export async function markNotificationUnread(id, reason, intentAuditEventId) {
  const response = await apiPost(`/admin/notifications/${id}/mark-unread`, {
    reason,
    intentAuditEventId,
  });
  return response.notification || response;
}

/**
 * Snooze notification
 * @param {string} id - Notification ID
 * @param {number} minutes - Minutes to snooze (30, 60, 240, 1440)
 * @param {string} reason - Reason for snoozing
 * @param {number} intentAuditEventId - Intent audit event ID
 * @returns {Promise<object>} Updated notification
 */
export async function snoozeNotification(id, minutes, reason, intentAuditEventId) {
  const response = await apiPost(`/admin/notifications/${id}/snooze`, {
    minutes,
    reason,
    intentAuditEventId,
  });
  return response.notification || response;
}

/**
 * Unsnooze notification
 * @param {string} id - Notification ID
 * @param {string} reason - Reason for unsnoozing
 * @param {number} intentAuditEventId - Intent audit event ID
 * @returns {Promise<object>} Updated notification
 */
export async function unsnoozeNotification(id, reason, intentAuditEventId) {
  const response = await apiPost(`/admin/notifications/${id}/unsnooze`, {
    reason,
    intentAuditEventId,
  });
  return response.notification || response;
}
